import React, { useCallback, useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useHistory, useLocation } from 'react-router-dom';
import MUIProvider from '~/contexts/MUIProvider';

import { Button, Card, CardContent, Container, Stack, Typography } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import Spinner from '~/components/Common/Spinner';

import { useDealCrawlerByIds } from '~/hooks/apis/cruises/useDealCrawlerByIds';

import { CruiseDealCrawler } from '~/services/cruises/DealCrawlerService';

import { paramToArray } from '~/utils/url';

import DealCrawlerPageCreationSelectionModal from './DealCrawlerPageCreationSelectionModal';
import {
  DEAL_CRAWLER_PAGE_CREATION_CONFIRM_PATH,
  DEAL_CRAWLER_PAGE_CREATION_SELECTION_PATH,
  buildDealCrawlerPageCreationSearch,
  getMainCrawlerIdFromSearch,
} from './paths';

export default function DealCrawlerPageCreationSelection() {
  const history = useHistory();
  const location = useLocation();
  const crawlerIds = useMemo(
    () => paramToArray(new URLSearchParams(location.search).get('crawlerIds') ?? undefined) ?? [],
    [location.search],
  );
  const mainCrawlerId = getMainCrawlerIdFromSearch(location.search, crawlerIds);
  const { dealCrawlerById, isLoading, isSuccess } = useDealCrawlerByIds(crawlerIds);
  const [selectionModalOpen, setSelectionModalOpen] = useState(false);
  const dealCrawlers = useMemo(
    () => crawlerIds.map((id) => dealCrawlerById[id]).filter((c): c is CruiseDealCrawler => Boolean(c)),
    [crawlerIds, dealCrawlerById],
  );

  const handleSetMainCrawler = useCallback(
    (crawlerId: string) => {
      const search = buildDealCrawlerPageCreationSearch({ crawlerIds, mainCrawlerId: crawlerId });
      history.replace(`${DEAL_CRAWLER_PAGE_CREATION_SELECTION_PATH}?${search}`);
    },
    [history, crawlerIds],
  );

  const handleAcceptMultipleCrawlers = useCallback(
    (selected: Array<CruiseDealCrawler>, mainCrawlerIdFromModal: string) => {
      const ids = selected.map((c) => c.id);
      const search = buildDealCrawlerPageCreationSearch({ crawlerIds: ids, mainCrawlerId: mainCrawlerIdFromModal });
      history.push(`${DEAL_CRAWLER_PAGE_CREATION_SELECTION_PATH}?${search}`);
    },
    [history],
  );

  const handleConfirmSelectedCrawlers = useCallback(() => {
    const search = buildDealCrawlerPageCreationSearch({ crawlerIds, mainCrawlerId });
    history.push(`${DEAL_CRAWLER_PAGE_CREATION_CONFIRM_PATH}?${search}`);
  }, [history, crawlerIds, mainCrawlerId]);

  if (!isSuccess) {
    return <Spinner />;
  }

  return (
    <MUIProvider>
      <Helmet>
        <title>Create Deal Page From Crawler Data</title>
      </Helmet>

      <Container maxWidth="xl">
        <PageHeader title="Deal Page from Crawler" backButton />

        <Stack direction="column" spacing={3}>
          <Typography component="h2" variant="h5">
            Select the crawlers you want to use to create the new deal page.
          </Typography>

          <Typography variant="body1" color="text.secondary">
            Choose one crawler as main (name and dates come from it). Other crawlers contribute only their departures.
          </Typography>

          <Stack direction="column" spacing={1}>
            {isLoading && (
              <Stack direction="row" justifyContent="center" alignItems="center" minHeight={200}>
                <Spinner />
              </Stack>
            )}

            {dealCrawlers.length > 0 &&
              !isLoading &&
              dealCrawlers.map((dealCrawler) => (
                <Card variant="outlined" key={dealCrawler.id}>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
                      <Stack spacing={1}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {dealCrawler.dealPageName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <a href={dealCrawler.sourceUrl} target="_blank" rel="noopener noreferrer">
                            {dealCrawler.sourceUrl}
                          </a>
                        </Typography>
                      </Stack>
                      {dealCrawler.id === mainCrawlerId ? (
                        <Typography variant="caption" color="primary" fontWeight="bold">
                          Main
                        </Typography>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          onClick={() => handleSetMainCrawler(dealCrawler.id)}
                        >
                          Set as main
                        </Button>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
          </Stack>

          <Stack direction="row" spacing={2}>
            <Button variant="outlined" color="primary" onClick={() => setSelectionModalOpen(true)}>
              Select more crawlers
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmSelectedCrawlers}
              disabled={crawlerIds.length === 0}
            >
              Confirm selected crawlers
            </Button>
          </Stack>
        </Stack>

        <DealCrawlerPageCreationSelectionModal
          open={selectionModalOpen}
          onClose={() => setSelectionModalOpen(false)}
          onConfirm={(selected, mainCrawlerIdFromModal) => {
            setSelectionModalOpen(false);
            handleAcceptMultipleCrawlers(selected, mainCrawlerIdFromModal);
          }}
          defaultVendorId={dealCrawlers[0]?.vendorId}
          selectedCrawlerIds={crawlerIds}
          mainCrawlerId={mainCrawlerId}
        />
      </Container>
    </MUIProvider>
  );
}
